import { ArrowCircleRight } from "@phosphor-icons/react";
import clsx from "clsx";

import { SubscriptionPlanIcon } from "features/subscriptions/components";

import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SubscriptionType } from "common/types";

import {
  ACTIVATE_PLAN_CONFIG,
  ACTIVATE_PLAN_UPSELL_ICON_COLORS,
  PLAN_COVER_IMAGES,
} from "../../constants";

interface ActivatePlanCardProps {
  plan: SubscriptionType;
  onActivate: () => void;
}

export default function ActivatePlanCard({
  plan,
  onActivate,
}: ActivatePlanCardProps) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    title,
    subtitle,
    price,
    pricePeriod,
    backgroundClassName,
    upsellCards,
  } = ACTIVATE_PLAN_CONFIG[plan];
  const upsellIconColors = ACTIVATE_PLAN_UPSELL_ICON_COLORS[plan];

  const mainComponent = (
    <div
      className={clsx(
        "flex flex-col justify-between bg-white bg-cover bg-center p-1.5 lg:p-5 rounded-3xl border border-black-200",
        backgroundClassName,
      )}
    >
      <div className="p-5 lg:p-0 flex flex-col gap-4 lg:gap-16.5 justify-between">
        <div className="flex flex-row justify-between lg:items-center">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
            <SubscriptionPlanIcon plan={plan} size="sm" />

            <div className="flex flex-col gap-1">
              <span className="text-lg-bold">{title}</span>
              <span className="text-sm-medium text-black-500">{subtitle}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1">
            <span className="text-2xl-bold">{price}</span>

            {pricePeriod && (
              <span className="text-sm-medium text-black-400">
                {pricePeriod}
              </span>
            )}
          </div>
        </div>

        <div className="lg:hidden h-px w-full bg-black-200"></div>

        <div className="flex lg:hidden flex-col">
          {upsellCards.map(({ id, title: upsellTitle, icon }) => {
            const Icon = icon;

            return (
              <div className="flex py-2 flex-row gap-5 items-center" key={id}>
                <div className={clsx("p-1.5 rounded-xl", upsellIconColors.bg)}>
                  <Icon className={clsx("size-4", upsellIconColors.text)} />
                </div>

                <span className="text-sm-medium">{upsellTitle}</span>
              </div>
            );
          })}
        </div>

        <RestrictedComponent visibleFor={["billing_full"]}>
          <Button
            size="sm"
            className="ml-auto"
            rightIcon={<ArrowCircleRight weight="fill" />}
            onClick={onActivate}
          >
            Activate subscription
          </Button>
        </RestrictedComponent>
      </div>

      <img
        src={PLAN_COVER_IMAGES[plan]}
        className="block lg:hidden max-h-80 rounded-3xl w-full h-full object-cover"
        alt=""
      />
    </div>
  );

  if (isDesktop) {
    return (
      <div className="p-1 rounded-4xl border border-black-200">
        {mainComponent}
      </div>
    );
  }

  return mainComponent;
}
